import { useVerify } from '@entities/Authentication/model.ts';
import {
  useChangeEmailMutation,
  useChangePhoneMutation,
  useRegisterMutation,
} from '@entities/Authentication/rtk-query.ts';
import { useSaveData } from '@entities/common/savedData';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, parse } from 'date-fns';
import { useCallback, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useCaptchaModal } from '@pages/personal/client/editor-page/Modals/CaptchaVerificationModal/model.ts';
import { useOpenEmailVerify } from '@pages/personal/client/editor-page/Modals/VerifyEmail/model.ts';
import { useOpenPhoneVerify } from '@pages/personal/client/editor-page/Modals/VerifyPhone/model.ts';
import { useEditorClientSharedState } from '@pages/personal/client/editor-page/model.ts';
import { useUpdateMe } from '@pages/personal/model.ts';
import { contactSchema } from '@widgets/contacts/contact-edit/constans.tsx';
import { useAuthModal } from '@features/common/protected-auth-route/model.ts';
import {
  useAppDispatch,
  useAuthToken,
  usePlatformSettings,
  usePopup,
  useUser,
} from '@shared/hooks';
import { useCloseFormDialogModal } from '@shared/hooks/useCloseFormDialogModal/useCloseFormDialogModal.ts';
import { type ContactItem } from '@shared/types/contacts';
import { usePhoneVerificationIncomingModal } from '@shared/ui/modals/phone-verification-incoming-modal/helpers.ts';
import { useRegistrationRedirect } from '@src/library/hooks/user/helpers.ts';

const getDifferentValues = <T>({
  firstObject,
  secondObject,
}: {
  firstObject: Partial<T>;
  secondObject: Partial<T>;
}) => {
  const differentValues: Partial<T> = {};
  for (const key in firstObject) {
    if (firstObject[key] !== secondObject[key]) {
      differentValues[key] = secondObject[key];
    }
  }
  return differentValues;
};

interface Inputs {
  last_name: string;
  first_name: string;
  middle_name?: string;
  email: string;
  photo?: string;
  phonenumber: string;
  // url?: string;
  birth_date?: Date;
  gender: 'm' | 'f';
  active_contacts: Pick<ContactItem, 'type' | 'value'>[];
}

const schema = yup.object({
  last_name: yup.string().required('Фамилия не заполнена'),
  first_name: yup.string().required('Имя не заполнено'),
  middle_name: yup.string(),
  email: yup
    .string()
    .required('E-mail не указан')
    .matches(/\S+@\S+\.\S+/, 'E-mail указан неверно'),
  photo: yup.string().nullable(),
  phonenumber: yup.string(),
  // .required('Телефон не указан'),
  url: yup.string(),
  birth_date: yup.date().required('Укажите дату рождения'),
  gender: yup.string().oneOf(['m', 'f'], 'Укажите ваш пол').required('Укажите ваш пол'),
  active_contacts: yup.array().of(contactSchema),
});

const useProfileEdit = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { popup } = usePopup();
  const { onVerificationButtonClick, isVerificationLoading } = useVerify();
  const { openCaptchaModal } = useCaptchaModal();
  const { openEmailVerificationModal } = useOpenEmailVerify();
  const { openPhoneVerificationModal } = useOpenPhoneVerify();
  const { openPhoneVerificationIncomingModal } = usePhoneVerificationIncomingModal();

  const { openSuccessAuthModal, openAuth } = useAuthModal();
  const { editorClientSharedState } = useEditorClientSharedState();
  const { doCloseFormWithDialogModal } = useCloseFormDialogModal();
  const [register, { isLoading: isRegisterLoading }] = useRegisterMutation();
  const { user, isAuth, refetchIAm } = useUser();
  const { first_method } = usePlatformSettings();
  // const { tempContacts } = useTempContacts();
  const { setToken } = useAuthToken();
  const { doRedirectOnPrevPage } = useRegistrationRedirect();
  const [changeEmail] = useChangeEmailMutation();
  const [changePhone] = useChangePhoneMutation();
  const { updateMe, isLoading: isUpdateMeLoading } = useUpdateMe();
  const {
    data: savedFormData,
    doSetStorageData: saveFormData,
    doRemoveStorageData,
  } = useSaveData<Inputs>('clientEditorFormSavedData');

  const [emailVerified, setEmailVerified] = useState(!!user?.email);
  const [phoneNumberVerified, setPhoneNumberVerified] = useState(
    !isAuth ? true : !!user?.phonenumber
  );

  const parsedUserData = {
    photo: user?.photo || '',
    last_name: user?.last_name || '',
    first_name: user?.first_name || '',
    middle_name: user?.middle_name || '',
    email: user?.email || '',
    phonenumber: user?.phonenumber || '',
    birth_date: user?.birth_date ? parse(user?.birth_date, 'yyyy-MM-dd', new Date()) : undefined,
    gender: user?.gender,
    active_contacts:
      user?.active_contacts?.map((contact) => ({
        id: contact.id,
        type: contact.type,
        value: contact.value,
      })) || [],
  };

  const getFormValues = () => {
    if (savedFormData) {
      return { ...savedFormData };
    } else if (user) {
      return parsedUserData;
    }
    return;
  };

  const methods = useForm<Inputs>({
    resolver: yupResolver<Inputs>(schema),
    defaultValues: {
      last_name: '',
      first_name: '',
      middle_name: '',
      email: '',
      phonenumber: editorClientSharedState?.phonenumber || '',
      birth_date: undefined,
      photo: '',
      active_contacts: [],
    },
    values: getFormValues(),
  });
  const updateCurrentUser = async (data: Partial<Inputs>) => {
    await updateMe({
      data: {
        ...getDifferentValues({ firstObject: parsedUserData, secondObject: data }),
        photo: data?.photo || null,
        birth_date: format(data.birth_date as Date, 'yyyy-MM-dd'),
        id: String(user?.id),
      },
      onError: ({ fieldName, message }) => {
        methods.setError(fieldName as keyof Inputs, {
          type: 'value',
          message: message,
        });
        dispatch(popup({ type: 'error', text: `Не удалось сохранить изменения` }));
      },
      onSuccess: () => {
        navigate('/personal');
        doRemoveStorageData();
        dispatch(popup({ type: 'success', text: `Изменения успешно сохранены` }));
      },
    });
  };

  const registerNewUser = async ({
    data,
    email_code,
    email,
  }: {
    data: Inputs;
    email: string;
    email_code: string;
  }) => {
    if (editorClientSharedState) {
      const { phonenumber, phonenumber_code, password } = editorClientSharedState;
      const response = await register({
        password,
        phonenumber: `+${phonenumber}`,
        phonenumber_code,
        email,
        email_code,
        photo: data?.photo || undefined,
        first_name: data.first_name,
        middle_name: data?.middle_name,
        last_name: data.last_name,
        gender: data.gender as 'm' | 'f',
        birth_date: format(data.birth_date as Date, 'yyyy-MM-dd'),
        active_contacts: data.active_contacts,
      });
      if (response?.error) {
        throw response;
      } else {
        setToken(response.data.token);

        doRedirectOnPrevPage('/', { replace: true });

        openSuccessAuthModal({
          title: 'Регистрация успешна',
          withoutReload: true,
        });
        refetchIAm();
      }
    }
  };
  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data) => {
      if (isAuth) {
        if (emailVerified && phoneNumberVerified) {
          await updateCurrentUser(data);
        } else {
          if (!emailVerified) {
            const verification_type = 'reset_email';
            if (first_method === 'INCOMING_SMS') {
              await onVerificationButtonClick({
                verification_type,
                phonenumber: user?.phonenumber,
                type: 'INCOMING_SMS',
                onSuccess: (verifyResponseData) => {
                  openPhoneVerificationIncomingModal({
                    number: verifyResponseData?.number,
                    phonenumber: user!.phonenumber,
                    passcode: verifyResponseData?.passcode,
                    disableOverlayCss: false,
                    onSuccess: () => {
                      onVerificationButtonClick({
                        verification_type,
                        email: data?.email,
                      });
                      openEmailVerificationModal({
                        verification_type,
                        value: data?.email,
                        onSuccessVerified: async ({ value: email, passCode: emailPassCode }) => {
                          await changeEmail({
                            email: email as string,
                            phonenumber_code: verifyResponseData?.passcode as string,
                            email_code: emailPassCode as string,
                            userId: String(user?.id),
                          });
                          await updateCurrentUser({
                            ...data,
                            phonenumber: undefined,
                            email: undefined,
                          });
                        },
                      });
                    },
                  });
                },
              });
            } else {
              openCaptchaModal({
                verification_type,
                onSuccess: () => {
                  openPhoneVerificationModal({
                    verification_type,
                    value: user?.phonenumber,
                    onSuccessVerified: ({ passCode: phoneNumberPassCode }) => {
                      openCaptchaModal({
                        verification_type,
                        email: data?.email,
                        onSuccess: () => {
                          openEmailVerificationModal({
                            verification_type,
                            value: data?.email,
                            onSuccessVerified: async ({
                              value: email,
                              passCode: emailPassCode,
                            }) => {
                              await changeEmail({
                                email: email as string,
                                phonenumber_code: phoneNumberPassCode as string,
                                email_code: emailPassCode as string,
                                userId: String(user?.id),
                              });
                              await updateCurrentUser({
                                ...data,
                                phonenumber: undefined,
                                email: undefined,
                              });
                            },
                          });
                        },
                      });
                    },
                  });
                },
              });
            }
          }
          if (!phoneNumberVerified) {
            const verification_type = 'reset_phone';

            if (first_method === 'INCOMING_SMS') {
              await onVerificationButtonClick({
                verification_type,
                email: data?.email,
                onSuccess: () => {
                  openEmailVerificationModal({
                    verification_type,
                    onSuccessVerified: ({ passCode: emailPassCode }) => {
                      onVerificationButtonClick({
                        verification_type,
                        phonenumber: data?.phonenumber,
                        type: 'INCOMING_SMS',
                        onSuccess: (verifyData) => {
                          openPhoneVerificationIncomingModal({
                            disableOverlayCss: false,
                            passcode: verifyData?.passcode,
                            number: verifyData?.number,
                            phonenumber: data?.phonenumber,
                            verification_type,
                            onSuccess: async () => {
                              await changePhone({
                                phonenumber: data?.phonenumber as string,
                                phonenumber_code: verifyData?.passcode as string,
                                email_code: emailPassCode as string,
                                userId: String(user?.id),
                              });
                              await updateCurrentUser({
                                ...data,
                                phonenumber: undefined,
                                email: undefined,
                              });
                            },
                          });
                        },
                      });
                    },
                  });
                },
              });
            } else {
              openCaptchaModal({
                verification_type,
                onSuccess: () => {
                  openEmailVerificationModal({
                    verification_type,
                    onSuccessVerified: ({ passCode: emailPassCode }) => {
                      openCaptchaModal({
                        verification_type,
                        phonenumber: data.phonenumber,
                        onSuccess: () => {
                          openPhoneVerificationModal({
                            verification_type,
                            value: data.phonenumber,
                            onSuccessVerified: async ({
                              value: phoneNumber,
                              passCode: phoneNumberPassCode,
                            }) => {
                              await changePhone({
                                phonenumber: phoneNumber as string,
                                phonenumber_code: phoneNumberPassCode as string,
                                email_code: emailPassCode as string,
                                userId: String(user?.id),
                              });
                              await updateCurrentUser({
                                ...data,
                                phonenumber: undefined,
                                email: undefined,
                              });
                            },
                          });
                        },
                      });
                    },
                  });
                },
              });
            }
          }
        }
      } else {
        if (editorClientSharedState?.email_code) {
          await registerNewUser({
            data,
            email: data?.email as string,
            email_code: editorClientSharedState?.email_code as string,
          });
        } else {
          await openEmailVerificationModal({
            verification_type: 'registration',
            value: data?.email,
            onAction: async () => {
              let status = Promise.resolve();
              await onVerificationButtonClick({
                verification_type: 'registration',
                email: data?.email,
                onError: (errMessage) => {
                  methods.setError('email', { type: 'value', message: errMessage });
                  status = Promise.reject();
                },
              });
              return status;
            },
            onSuccessVerified: async ({ passCode: email_code, value: email }) => {
              await registerNewUser({
                data,
                email: email as string,
                email_code: email_code as string,
              });
            },
          });
        }
      }
    },
    [
      changeEmail,
      changePhone,
      isAuth,
      openCaptchaModal,
      openEmailVerificationModal,
      openPhoneVerificationModal,
      phoneNumberVerified,
      user,
      emailVerified,
      editorClientSharedState,
    ]
  );

  const onSubmitHandler = useCallback(() => {
    // иденцифицирует о том, что есть все данные
    if ((editorClientSharedState && Object.keys(editorClientSharedState).length >= 3) || isAuth) {
      methods.handleSubmit(onSubmit)();
      // console.log('weAuth');
    } else {
      navigate('/');
      openAuth();
    }
  }, [editorClientSharedState, isAuth, methods, navigate, onSubmit, openAuth]);

  const doSaveFormData = useCallback(() => {
    saveFormData(methods.getValues());
  }, [saveFormData, methods]);

  return {
    onPageLeave: doCloseFormWithDialogModal({
      isShouldModalOpen: Boolean(editorClientSharedState || methods.formState.isDirty),
      text: isAuth
        ? 'Внесенные изменения не сохранятся'
        : 'Если уйти с формы регистрации, введённые данные не сохранятся',
      title: isAuth ? 'Закрыть страницу?' : 'Регистрация не завершена',
      onModalConfirm: () => {
        navigate(isAuth ? '/personal' : '/');
      },
    }),
    isLoading: isRegisterLoading || isUpdateMeLoading || isVerificationLoading,
    user,
    onSubmit,
    emailVerified,
    setEmailVerified,
    phoneNumberVerified,
    setPhoneNumberVerified,
    onSubmitHandler,
    doSaveFormData,
    defaultValues: getFormValues(),
    isAuth,
    parsedUserData,
    ...methods,
  };
};

export default useProfileEdit;
